import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function getSupabaseAdmin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

/**
 * Returns whether any admin user already exists.
 * Used to gate the signup option on the login page — only the very first
 * user can sign up (and is auto-promoted to admin via the
 * handle_new_admin_user DB trigger). After that, signup is hidden.
 */
export const hasAnyAdmin = createServerFn({ method: "GET" }).handler(async () => {
  const supabaseAdmin = await getSupabaseAdmin();
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("user_id")
    .in("role", ["admin", "super_admin"])
    .limit(1);
  if (error) {
    console.error("[hasAnyAdmin] query failed:", error);
    // Fail closed: pretend an admin exists so signup stays hidden.
    return { hasAdmin: true };
  }
  return { hasAdmin: (data?.length ?? 0) > 0 };
});

// NOTE: We intentionally do NOT use requireSupabaseAuth here. This server fn
// is polled by use-auth on every session restore, including transient states
// where the bearer token may not yet be attached. Throwing "Unauthorized"
// surfaces as an unhandled runtime error in the preview. Instead we read the
// header manually and gracefully return { isAdmin: false } when missing.
export const getCurrentAdminStatus = createServerFn({ method: "GET" }).handler(
  async () => {
    const authHeader = getRequestHeader("authorization");
    if (!authHeader || !authHeader.startsWith("Bearer ")) {
      return { isAdmin: false };
    }
    const token = authHeader.slice("Bearer ".length).trim();
    if (!token) return { isAdmin: false };

    const supabaseAdmin = await getSupabaseAdmin();

    const { data: userData, error: userError } = await supabaseAdmin.auth.getUser(token);
    if (userError || !userData?.user?.id) {
      return { isAdmin: false };
    }
    const userId = userData.user.id;

    const { data, error } = await supabaseAdmin
      .from("user_roles")
      .select("user_id")
      .eq("user_id", userId)
      .in("role", ["admin", "super_admin"])
      .limit(1);

    if (error) {
      console.error("[getCurrentAdminStatus] query failed:", { error, userId });
      return { isAdmin: false };
    }

    return { isAdmin: (data?.length ?? 0) > 0 };
  },
);

export const claimFirstAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    const supabaseAdmin = await getSupabaseAdmin();

    const [
      { data: existingAdmins, error: adminError },
      { data: currentAdminRole, error: currentRoleError },
      { data: currentSuperAdminRole, error: currentSuperRoleError },
    ] = await Promise.all([
      supabaseAdmin.from("user_roles").select("user_id, created_at").eq("role", "admin").order("created_at", { ascending: true }).limit(2),
      supabaseAdmin.from("user_roles").select("user_id").eq("user_id", userId).eq("role", "admin").maybeSingle(),
      supabaseAdmin.from("user_roles").select("user_id").eq("user_id", userId).eq("role", "super_admin").maybeSingle(),
    ]);

    if (adminError || currentRoleError || currentSuperRoleError) {
      console.error("[claimFirstAdmin] failed to read admin state", { adminError, currentRoleError, currentSuperRoleError });
      throw new Error("could not verify admin setup right now");
    }

    if ((existingAdmins?.length ?? 0) > 0) {
      if (currentAdminRole) {
        const isOnlyBootstrapAdmin = (existingAdmins?.length ?? 0) === 1 && existingAdmins?.[0]?.user_id === userId;

        if (!currentSuperAdminRole && isOnlyBootstrapAdmin) {
          const { error: bootstrapSuperRoleError } = await supabaseAdmin
            .from("user_roles")
            .upsert({ user_id: userId, role: "super_admin" }, { onConflict: "user_id,role", ignoreDuplicates: true });

          if (bootstrapSuperRoleError) {
            console.error("[claimFirstAdmin] failed to repair missing super admin role", { bootstrapSuperRoleError, userId });
            throw new Error("could not finish first admin setup");
          }

          return { claimed: true, alreadyAdmin: true };
        }

        return { claimed: false, alreadyAdmin: true };
      }
      throw new Error("an admin already exists. sign in with an admin account.");
    }

    const { data: userResult, error: userError } = await supabaseAdmin.auth.admin.getUserById(userId);
    const email = userResult.user?.email?.trim().toLowerCase();

    if (userError || !email) {
      console.error("[claimFirstAdmin] failed to load authenticated user", { userError, userId });
      throw new Error("could not load your account email");
    }

    const [{ error: allowlistError }, { error: roleError }, { error: superRoleError }] = await Promise.all([
      supabaseAdmin.from("admin_allowlist").upsert({ email }, { onConflict: "email", ignoreDuplicates: true }),
      supabaseAdmin
        .from("user_roles")
        .upsert({ user_id: userId, role: "admin" }, { onConflict: "user_id,role", ignoreDuplicates: true }),
      // The very first admin is automatically promoted to super_admin so they
      // can manage other admins and get the gold "Super admin" badge.
      supabaseAdmin
        .from("user_roles")
        .upsert({ user_id: userId, role: "super_admin" }, { onConflict: "user_id,role", ignoreDuplicates: true }),
    ]);

    if (allowlistError || roleError || superRoleError) {
      console.error("[claimFirstAdmin] failed to grant admin role", { allowlistError, roleError, superRoleError, userId, email });
      throw new Error("could not finish first admin setup");
    }

    return { claimed: true, alreadyAdmin: false };
  });
